import { RefObject, useCallback, useEffect, useRef } from 'react';

import { ActivatorElementType } from '@hh.ru/magritte-internal-drop-base/types';

// теоретически на один активатор может быть завязано несколько дропов (например дроп и тултип)
// поэтому храним коллекцию коллбеков для каждого активатора
const intersectionCallbacks = new Map<ActivatorElementType, VoidFunction[]>();
let observer: IntersectionObserver;
// Т.к. IntersectionObserver при первом вызове observe(element) сразу вызывает коллбек для элемента
// необходимо игнорировать первый вызов коллбека после вызова observe, т.к. о изменении состояния сигнализирует только
// второй вызов коллбека. Для этого используем set элементов, для которых был вызван observe, но еще не был вызван коллбек
const ignoreImmediateCallback = new Set<ActivatorElementType>();

const notifyAndClearSubscriptions = (element: HTMLElement) => {
    if (ignoreImmediateCallback.has(element)) {
        ignoreImmediateCallback.delete(element);
        return;
    }
    const callbacks = intersectionCallbacks.get(element);
    if (callbacks) {
        callbacks.forEach((callback) => callback());
    }
    intersectionCallbacks.delete(element);
    observer.unobserve(element);
};

const initialize = () => {
    if (observer) {
        return;
    }

    observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            notifyAndClearSubscriptions(entry.target as HTMLElement);
        });
    });
};

const observeIntersectionsOfElement = (element: ActivatorElementType, callback: VoidFunction) => {
    initialize();
    if (!intersectionCallbacks.has(element)) {
        intersectionCallbacks.set(element, []);
        ignoreImmediateCallback.add(element);
        observer.observe(element);
    }
    intersectionCallbacks.get(element)?.push(callback);
};

const unobserveIntersectionsOfElement = (element: ActivatorElementType | null, callback: VoidFunction) => {
    if (!element) {
        return;
    }
    const callbacks = intersectionCallbacks.get(element);
    if (!callbacks) {
        return;
    }
    const filteredCallbacks = callbacks.filter((cb) => cb !== callback);
    if (filteredCallbacks.length === 0) {
        intersectionCallbacks.delete(element);
        observer.unobserve(element);
        return;
    }

    intersectionCallbacks.set(element, filteredCallbacks);
};

export const useIntersectionSubscription = (
    activatorRef: RefObject<ActivatorElementType>,
    isVisible: boolean,
    ignore: boolean,
    callback: VoidFunction
): void => {
    const intersectionCallbackRef = useRef(callback);
    intersectionCallbackRef.current = callback;

    const intersectionCallback = useCallback(() => intersectionCallbackRef.current(), []);

    useEffect(() => {
        if (!activatorRef.current || !isVisible || ignore) {
            return;
        }
        const target = activatorRef.current;
        observeIntersectionsOfElement(target, intersectionCallback);
        // eslint-disable-next-line consistent-return
        return () => {
            unobserveIntersectionsOfElement(target, intersectionCallback);
        };
    }, [isVisible, activatorRef, intersectionCallback, ignore]);
};
